import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader";
import FlexTable from "../../components/ui-components/FlexTable";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import Badge from "../../components/ui-components/Badge";
import RightSheet from "../../components/ui-components/RightSheet";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles";
import { toast } from "../../components/ui-components/Toast";
import {
  listProgramme,
  createProgrammeItem,
  updateProgrammeItem,
  deleteProgrammeItem,
  listCandidates,
} from "./api";

const eurFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "EUR",
});

const formatEur = (value) => (typeof value === "number" ? eurFormatter.format(value) : "—");

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const STATUS_OPTIONS = [
  { value: "potential", label: "Potential" },
  { value: "committed", label: "Committed" },
  { value: "realized", label: "Realized" },
  { value: "dismissed", label: "Dismissed" },
];

// Badge doesn't know our 4 statuses (potential/committed/realized/dismissed) — map
// each to a Badge tone key explicitly rather than relying on its fallback (which would
// render every status the same neutral gray).
const STATUS_BADGE_TONE = {
  potential: "neutral",
  committed: "info",
  realized: "success",
  dismissed: "danger",
};

function StatusBadge({ status }) {
  const key = status || "potential";
  const label = STATUS_OPTIONS.find((s) => s.value === key)?.label || key;
  return <Badge status={STATUS_BADGE_TONE[key] || "neutral"} label={label} />;
}

// ── Realization summary body ─────────────────────────────────────

function SummaryBody({ totals, itemCount }) {
  const { committed, realized, byStatus } = totals;
  const pct = committed > 0 ? Math.min(100, Math.round((realized / committed) * 100)) : 0;

  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="flex flex-wrap gap-8">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-text-2">Committed savings</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-text">{eurFormatter.format(committed)}</div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-text-2">Realized savings</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-success">{eurFormatter.format(realized)}</div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-text-2">Items</div>
          <div className="mt-1 text-2xl font-semibold tabular-nums text-text">{itemCount}</div>
        </div>
      </div>

      {committed > 0 && (
        <div>
          <div className="h-2 w-full max-w-md overflow-hidden rounded-full bg-surface-2">
            <div className="h-full rounded-full bg-success" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1 text-xs text-text-2">{pct}% of committed savings realized</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((s) => (
          <span
            key={s.value}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-2 px-2.5 py-1 text-xs text-text-2"
          >
            {s.label}: <span className="font-semibold text-text">{byStatus[s.value] || 0}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Edit sheet ──────────────────────────────────────────────────

function ProgrammeEditSheet({ item, onClose, onSave, saving }) {
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState("potential");
  const [committedSaving, setCommittedSaving] = useState("");
  const [realizedSaving, setRealizedSaving] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!item) return;
    setOwner(item.owner || "");
    setStatus(item.status || "potential");
    setCommittedSaving(item.committed_saving != null ? String(item.committed_saving) : "");
    setRealizedSaving(item.realized_saving != null ? String(item.realized_saving) : "");
    setTargetDate(item.target_date ? String(item.target_date).slice(0, 10) : "");
    setNotes(item.notes || "");
  }, [item]);

  const handleSubmit = () => {
    onSave({
      recommendation_ref: item.recommendation_ref,
      title: item.title,
      owner,
      status,
      committed_saving: committedSaving === "" ? 0 : Number(committedSaving),
      realized_saving: realizedSaving === "" ? 0 : Number(realizedSaving),
      target_date: targetDate || null,
      notes,
    });
  };

  return (
    <RightSheet
      open={!!item}
      onClose={onClose}
      title="Edit programme item"
      subtitle={item?.title}
      footer={
        <div className="flex items-center justify-end gap-3">
          <button type="button" className={secondaryButtonCls} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryButtonCls} onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      {item && (
        <div className="space-y-4">
          <div>
            <label htmlFor="opt-owner" className="text-xs font-medium text-text-2">
              Owner
            </label>
            <input
              id="opt-owner"
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="Owner"
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label htmlFor="opt-status" className="text-xs font-medium text-text-2">
              Status
            </label>
            <select
              id="opt-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="opt-committed" className="text-xs font-medium text-text-2">
                Committed saving (EUR)
              </label>
              <input
                id="opt-committed"
                type="number"
                step="0.01"
                value={committedSaving}
                onChange={(e) => setCommittedSaving(e.target.value)}
                className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="opt-realized" className="text-xs font-medium text-text-2">
                Realized saving (EUR)
              </label>
              <input
                id="opt-realized"
                type="number"
                step="0.01"
                value={realizedSaving}
                onChange={(e) => setRealizedSaving(e.target.value)}
                className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label htmlFor="opt-target-date" className="text-xs font-medium text-text-2">
              Target date
            </label>
            <input
              id="opt-target-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label htmlFor="opt-notes" className="text-xs font-medium text-text-2">
              Notes
            </label>
            <textarea
              id="opt-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}
    </RightSheet>
  );
}

// ── Main component ──────────────────────────────────────────────

const OptimizationProgrammePage = () => {
  const queryClient = useQueryClient();
  const [editingItem, setEditingItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [promotingKey, setPromotingKey] = useState(null);
  const [promotedKeys, setPromotedKeys] = useState(() => new Set());

  const {
    data: programme,
    isLoading: programmeLoading,
    error: programmeError,
    refetch: refetchProgramme,
  } = useQuery(["opt-programme"], listProgramme);

  const {
    data: candidates,
    isLoading: candidatesLoading,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useQuery(["opt-candidates"], listCandidates);

  const items = useMemo(() => programme || [], [programme]);
  const candidateItems = candidates || [];

  const totals = useMemo(() => {
    const committed = items.reduce((s, i) => s + (i.committed_saving || 0), 0);
    const realized = items.reduce((s, i) => s + (i.realized_saving || 0), 0);
    const byStatus = items.reduce((acc, i) => {
      const key = i.status || "potential";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return { committed, realized, byStatus };
  }, [items]);

  const refreshProgramme = () => queryClient.invalidateQueries({ queryKey: ["opt-programme"] });

  const handleSaveEdit = async (body) => {
    if (!editingItem) return;
    setSavingEdit(true);
    try {
      await updateProgrammeItem(editingItem.id, body);
      toast.success("Programme item updated");
      setEditingItem(null);
      refreshProgramme();
    } catch (err) {
      toast.error(err?.message || "Failed to update item");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (item) => {
    if (typeof window !== "undefined" && !window.confirm(`Delete "${item.title}" from the programme?`)) {
      return;
    }
    try {
      await deleteProgrammeItem(item.id);
      toast.success("Programme item deleted");
      refreshProgramme();
    } catch (err) {
      toast.error(err?.message || "Failed to delete item");
    }
  };

  const handlePromote = async (candidate) => {
    // NOTE — recommendation_ref limitation: /get-insights does not return a recommender
    // `name`, but the dbt EXTERNAL_QUERY view (int_f_insights) joins the programme on
    // recommendation_ref. We seed it from resource_md5 (falling back to id) as the best
    // available stable identifier. A perfect match to that dbt join is a deploy-data
    // nicety, not guaranteed here — the created item still works standalone regardless.
    const key = candidate.resource_md5 || candidate.id;
    setPromotingKey(key);
    try {
      await createProgrammeItem({
        recommendation_ref: key,
        title: candidate.insight || candidate.resource_name || "Optimization",
        committed_saving: candidate.costs || 0,
        status: "potential",
      });
      toast.success("Promoted to programme");
      setPromotedKeys((prev) => new Set(prev).add(key));
      refreshProgramme();
    } catch (err) {
      toast.error(err?.message || "Failed to promote candidate");
    } finally {
      setPromotingKey(null);
    }
  };

  const programmeColumns = useMemo(
    () => [
      {
        accessor: "title",
        Header: () => <span>Title</span>,
        Cell: ({ row }) => (
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-text truncate">{row.original.title || "Untitled"}</span>
            {row.original.recommendation_ref && (
              <span className="text-xs text-text-3 truncate">{row.original.recommendation_ref}</span>
            )}
          </div>
        ),
        flex: 1,
        minWidth: 220,
      },
      {
        accessor: "owner",
        Header: () => <span>Owner</span>,
        Cell: ({ value }) => <span className="text-text">{value || "—"}</span>,
        width: 140,
        minWidth: 100,
        maxWidth: 200,
      },
      {
        accessor: "status",
        align: "center",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusBadge status={value} />,
        width: 130,
        minWidth: 110,
        maxWidth: 160,
      },
      {
        accessor: "committed_saving",
        id: "committed",
        align: "right",
        Header: () => <span>Committed</span>,
        Cell: ({ value }) => <span className="tabular-nums text-text">{formatEur(value)}</span>,
        width: 130,
        minWidth: 110,
        maxWidth: 170,
      },
      {
        accessor: "realized_saving",
        id: "realized",
        align: "right",
        Header: () => <span>Realized</span>,
        Cell: ({ row }) => {
          const { realized_saving, estimated_realized_saving, status } = row.original;
          const showEstimate = status === "committed" && typeof estimated_realized_saving === "number";
          return (
            <span className="tabular-nums text-text">
              {formatEur(realized_saving)}
              {showEstimate && (
                <span className="ml-1 text-xs text-text-3">(est {formatEur(estimated_realized_saving)})</span>
              )}
            </span>
          );
        },
        width: 170,
        minWidth: 140,
        maxWidth: 230,
      },
      {
        accessor: "target_date",
        id: "target_date",
        Header: () => <span>Target date</span>,
        Cell: ({ value }) => <span className="tabular-nums text-text-2">{formatDate(value)}</span>,
        width: 130,
        minWidth: 110,
        maxWidth: 160,
      },
      {
        id: "actions",
        align: "center",
        Header: () => null,
        Cell: ({ row }) => (
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditingItem(row.original);
              }}
              className="text-lg px-2 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors cursor-pointer"
              title="Edit item"
            >
              <FontAwesomeIcon icon={icon({ name: "edit", style: "light" })} className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original);
              }}
              className="text-lg px-2 text-text-2 hover:text-danger hover:bg-surface-2 rounded-ctrl transition-colors cursor-pointer"
              title="Delete item"
            >
              <FontAwesomeIcon icon={icon({ name: "trash", style: "light" })} className="w-4 h-4" />
            </button>
          </div>
        ),
        width: 100,
        minWidth: 90,
        maxWidth: 120,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const candidateColumns = useMemo(
    () => [
      {
        id: "insight",
        Header: () => <span>Insight</span>,
        Cell: ({ row }) => (
          <span className="text-text truncate block">
            {row.original.insight || row.original.resource_name || "Optimization"}
          </span>
        ),
        flex: 1,
        minWidth: 260,
      },
      {
        id: "type",
        Header: () => <span>Type</span>,
        Cell: ({ row }) => (
          <span className="text-text-2">{row.original.resource_type || row.original.insight_group || "—"}</span>
        ),
        width: 160,
        minWidth: 120,
        maxWidth: 220,
      },
      {
        id: "saving",
        align: "right",
        Header: () => <span>Projected saving</span>,
        Cell: ({ row }) => <span className="tabular-nums text-text">{formatEur(row.original.costs)}</span>,
        width: 160,
        minWidth: 130,
        maxWidth: 200,
      },
      {
        id: "action",
        align: "center",
        Header: () => null,
        Cell: ({ row }) => {
          const key = row.original.resource_md5 || row.original.id;
          const isPromoted = promotedKeys.has(key);
          const isPromoting = promotingKey === key;
          return (
            <button
              type="button"
              disabled={isPromoted || isPromoting}
              onClick={(e) => {
                e.stopPropagation();
                handlePromote(row.original);
              }}
              className="inline-flex items-center gap-1.5 rounded-ctrl bg-primary px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPromoted ? "Promoted" : isPromoting ? "Promoting…" : "Promote to programme"}
            </button>
          );
        },
        width: 210,
        minWidth: 170,
        maxWidth: 240,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promotingKey, promotedKeys]
  );

  return (
    <Fragment>
      <AdministrationHeader title="Optimization Programme" showPeriodSelector={false} />
      <div className="w-full p-8 space-y-6">
        <TableCard
          title="Realization summary"
          subtitle="Committed vs realized savings across the programme"
          isLoading={programmeLoading}
          error={programmeError}
          errorMessage="Error loading programme totals"
          onRetry={() => refetchProgramme()}
        >
          <SummaryBody totals={totals} itemCount={items.length} />
        </TableCard>

        <TableCard
          title="Programme"
          subtitle={programmeLoading ? "Loading…" : `${items.length} tracked ${items.length === 1 ? "item" : "items"}`}
          isLoading={programmeLoading}
          error={programmeError}
          errorMessage="Error loading programme"
          onRetry={() => refetchProgramme()}
        >
          {items.length === 0 ? (
            <EmptyState
              icon={icon({ name: "list-check", style: "light" })}
              title="No programme items yet"
              message="Promote a candidate below to start tracking committed and realized savings."
            />
          ) : (
            <FlexTable columns={programmeColumns} data={items} getRowId={(i) => i.id} />
          )}
        </TableCard>

        <TableCard
          title="Candidates"
          subtitle="Recommender insights not yet in the programme"
          isLoading={candidatesLoading}
          error={candidatesError}
          errorMessage="Error loading candidates"
          onRetry={() => refetchCandidates()}
        >
          {candidateItems.length === 0 ? (
            <EmptyState
              icon={icon({ name: "lightbulb", style: "light" })}
              title="No candidates"
              message="New recommender insights will appear here for promotion into the programme."
            />
          ) : (
            <FlexTable
              columns={candidateColumns}
              data={candidateItems}
              getRowId={(c) => String(c.resource_md5 || c.id)}
            />
          )}
        </TableCard>
      </div>

      <ProgrammeEditSheet
        item={editingItem}
        onClose={() => setEditingItem(null)}
        onSave={handleSaveEdit}
        saving={savingEdit}
      />
    </Fragment>
  );
};

export default OptimizationProgrammePage;
